import * as THREE from 'three';
import { RAGNA } from '@somnhold/content';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Ragna's own effects (crimson): a wake of embers as she runs, the chop of her axe, the leap's trail and its landing,
 * the horn's wave, the cry and the whirlwind of the lone sally.
 */
const K = WORLD_PER_HEX / 1000;
const GLOW = '#ff3a6a';
const ACCENT = '#ff1f50';
const PALE = '#ffb0c4';
const SMOKE = '#24141a';

export const ragnaFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: '#1a0a10', smoke: SMOKE },
  clips: { ragnaLeap: 'leap', ragnaHorn: 'horn' },
  frame: (c, s, e, p, yaw, h) => {
    const u = e.un;
    if (!u) return;
    const beat = (s.tick + e.id) % 12;
    const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    // Quick feet: a footfall every few ticks, a spark trailing behind.
    if (e.anim === 'walk' && u.busy === 0 && beat % 4 === 0) {
      const side = beat % 8 === 0 ? 1 : -1;
      c.vfx.ring(p.clone().add(new THREE.Vector3(fwd.z * side * 0.25, 0.02, -fwd.x * side * 0.25)), 0.04, 0.28, ACCENT, 10, 0.55);
    }
    // Leaping: a trail of embers through the air.
    if (u.busy > 0 && u.k[0] === 1) c.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: c.low ? 2 : 5, color: GLOW, speed: 0.03, up: 0.02, size: 0.1, life: 10, gravity: 0.004 });
    // The sally: a ring of the axe's edge round her, embers flung out, soot.
    if (u.busy > 0 && u.k[0] === 2) {
      const R = RAGNA.sally.radius * K * 0.7;
      const a = s.tick * 1.4;
      const q = p.clone().add(new THREE.Vector3(Math.cos(a) * R, h * 0.45, Math.sin(a) * R));
      c.vfx.burst(q, { n: c.low ? 2 : 5, color: PALE, speed: 0.08, up: 0.03, size: 0.08, life: 8, gravity: 0.008 });
      c.vfx.burst(p.clone().setY(p.y + 0.25), { n: 1, color: SMOKE, speed: 0.05, up: 0.02, size: 0.6, life: 14, smoke: true, spread: 0.3 });
    }
    if (e.anim === 'attack' && e.animT === 2 && u.cast === 0 && u.busy === 0) {
      const hit = p.clone().addScaledVector(fwd, 0.9);
      c.vfx.ring(hit, 0.1, 0.9, ACCENT, 7, 1);
      c.vfx.burst(hit.clone().setY(hit.y + 0.2), { n: c.low ? 4 : 9, color: PALE, speed: 0.07, up: 0.08, size: 0.08, life: 8, gravity: 0.012 });
      c.shake(0.08);
    }
  },
  skill: (c, _s, _e, name, p) => {
    if (name !== 'ragnaLeap') return false;
    // She crouches and springs: dust and sparks where she took off.
    c.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, 0.9, ACCENT, 8, 0.8);
    c.vfx.burst(p.clone().setY(p.y + 0.2), { n: c.low ? 3 : 8, color: SMOKE, speed: 0.05, up: 0.04, size: 0.4, life: 12, smoke: true, spread: 0.3 });
    return true;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    if (kind === 'ragnaBlast') {
      // She lands: the axe into the ground, a ring of crimson, stone and soot thrown up.
      // Out to the blast's own line: `r` comes in world units (times K again it shrank to nothing).
      const R = r;
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, R, ACCENT, 10, 1);
      c.vfx.ring(p.clone().setY(p.y + 0.08), 0.1, R * 0.55, PALE, 8, 0.8);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: '#3a2c2a', speed: 0.08, up: 0.06, size: 0.5, life: 16, smoke: true, spread: R * 0.4 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 14, color: PALE, speed: 0.11, up: 0.1, size: 0.08, life: 10, gravity: 0.014 });
      c.flare(p.clone().setY(p.y + 0.5), GLOW, 3, 8);
      c.shake(0.3);
      return true;
    }
    if (kind === 'ragnaHorn') {
      // The horn's wave: rings running out over her reach, the air shaking.
      const R = r;
      c.vfx.ring(p.clone().setY(p.y + 0.1), 0.3, R, GLOW, 14, 0.9);
      c.vfx.ring(p.clone().setY(p.y + 0.6), 0.2, R * 0.7, PALE, 12, 0.6);
      c.flare(p.clone().setY(p.y + 1.2), GLOW, 3, 12);
      c.shake(0.2);
      return true;
    }
    if (kind === 'ragnaCry') {
      // The cry that starts the sally.
      if (e) c.force(e.id, 'sally');
      c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, r, ACCENT, 14, 1);
      c.vfx.shaft(p, p.clone().setY(p.y + 4), GLOW, 12, 0.8);
      c.vfx.burst(p.clone().setY(p.y + 1), { n: low ? 8 : 20, color: PALE, speed: 0.1, up: 0.08, size: 0.09, life: 14, gravity: 0.008 });
      c.flare(p.clone().setY(p.y + 1), GLOW, 5, 14);
      c.shake(0.5);
      return true;
    }
    if (kind === 'ragnaWhirl') {
      const R = r;
      c.vfx.ring(p.clone().setY(p.y + 0.15), R * 0.3, R, ACCENT, 6, 0.85);
      c.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 3 : 8, color: GLOW, speed: 0.1, up: 0.03, size: 0.08, life: 8, gravity: 0.008, spread: R * 0.4 });
      c.shake(0.12);
      return true;
    }
    return false;
  },
};
